import type { TableFilterState, TableSortingState } from '@ibsys/kit';
import { useMemo } from 'react';
import type { SampleFilters } from '@/entities/sample';
import type { SearchDialogsPage, SearchQueryRequest } from '@/entities/search';
import type { TableContentSearchState } from '@/entities/settings';
import { stableStringify } from '@/shared/lib';

/** Построитель среза выборки — тот же, каким список запрашивает свои страницы */
type BuildRequestParams = (params?: {
  filters?: TableFilterState;
  sorting?: TableSortingState;
  dialogsPage?: SearchDialogsPage;
}) => Omit<SearchQueryRequest, 'limit' | 'offset'>;

type Params = {
  /** Построитель среза выборки из состояния поиска списка */
  buildRequestParams: BuildRequestParams;
  /** Отбор, заданный пользователем: условия самого раздела в снимок не попадают */
  userFilters: TableFilterState;
  /** Режим и запрос поиска по содержимому — часть отбора, восстанавливаемая списком */
  contentSearch?: TableContentSearchState | null;
};

/** Снимок отбора и признак того, что он отличается от сохранённого в выборке */
type Result = {
  /** Текущий отбор в формате тела поиска — так же его читает и сервер, вычисляя состав выборки */
  snapshot: SampleFilters;
  /** Отбор задан: пустой снимок сохранять в выборку нечего */
  hasSelection: boolean;
  /** Отличается ли текущий отбор от переданного сохранённого */
  isChangedFrom: (saved?: SampleFilters | null) => boolean;
};

/** Отбор без постраничности и вида: сортировка и колонки — личный вид, в выборку они не идут */
const NO_SORTING: TableSortingState = [];
const NO_DIALOGS_PAGE: SearchDialogsPage = {};

/**
 * Сравниваются только условия отбора — остальное в снимке служебное.
 *
 * Незаданное условие всегда приводится к одному виду: сохранённый отбор возвращается
 * с пустыми значениями там, где список их вовсе не задавал, и без этого записанный отбор
 * навсегда считался бы отличным от показанного.
 */
const selectionKey = (filters?: SampleFilters | null): string =>
  stableStringify({
    filters: filters?.filters ?? undefined,
    dsl: filters?.dsl || undefined,
    dslHuman: filters?.dslHuman || undefined,
  });

/**
 * Снимок отбора для выборки по фильтрам. Собирается тем же построителем, что и запрос списка,
 * поэтому сохранённое в выборку и показанное в списке — один и тот же отбор.
 */
export const useSampleFiltersSnapshot = ({ buildRequestParams, userFilters, contentSearch }: Params): Result => {
  const snapshot = useMemo<SampleFilters>(() => {
    const params = buildRequestParams({
      filters: userFilters,
      sorting: NO_SORTING,
      dialogsPage: NO_DIALOGS_PAGE,
    });
    if (params.entityType !== 'conversation') return { contentSearch: contentSearch ?? null };
    return {
      filters: params.filters,
      dsl: params.dsl || undefined,
      dslHuman: params.dslHuman || undefined,
      contentSearch: contentSearch ?? null,
    };
  }, [buildRequestParams, userFilters, contentSearch]);

  return {
    snapshot,
    hasSelection: Boolean(snapshot.filters || snapshot.dsl || snapshot.dslHuman),
    isChangedFrom: (saved) => selectionKey(snapshot) !== selectionKey(saved),
  };
};
